import { ShippingLocation } from '@/types';
import { Search } from 'lucide-react';

interface LocationSelectorProps {
  locations: ShippingLocation[];
  selectedLocation: ShippingLocation | null;
  locationSearch: string;
  onLocationSearchChange: (value: string) => void;
  onLocationSelect: (location: ShippingLocation) => void;
  showLocationDropdown: boolean;
  onToggleLocationDropdown: () => void;
  selectedCountry: string;
  selectedState: string;
}

export function LocationSelector({
  locations,
  selectedLocation,
  locationSearch,
  onLocationSearchChange,
  onLocationSelect,
  showLocationDropdown,
  onToggleLocationDropdown,
  selectedCountry,
  selectedState,
}: LocationSelectorProps) {
  // Filter locations based on selected country, state, and search term
  const filteredLocations = locations.filter(
    (location) =>
      location.country === selectedCountry &&
      location.state === selectedState &&
      location.location.toLowerCase().includes(locationSearch.toLowerCase())
  );

  // Add Stock-Pile and Store-Pickup options
  const specialOptions = [
    { location: 'Store-Pickup', price: 0, country: selectedCountry, state: selectedState },
  ];

  return (
    <div className="relative">
      <label className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-2">
        Delivery Area
      </label>
      <button
        type="button"
        onClick={onToggleLocationDropdown}
        className="w-full h-12 px-4 text-left rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        disabled={!selectedCountry || !selectedState}
      >
        {selectedLocation 
          ? (selectedLocation.location === 'All Location' ? 'Home Delivery' : selectedLocation.location)
          : 'Select Delivery Area'
        }
      </button>

      {showLocationDropdown && selectedCountry && selectedState && (
        <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="p-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Search locations..."
                value={locationSearch}
                onChange={(e) => onLocationSearchChange(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="w-full pl-10 pr-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
            </div>
          </div>
          <div className="max-h-48 overflow-auto">
            {/* Render special options first */}
            {specialOptions.map((option) => (
              <button
                key={option.location}
                onClick={() => {
                  onLocationSelect(option);
                  onToggleLocationDropdown();
                }}
                className={`w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 ${
                  selectedLocation?.location === option.location
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                    : 'text-gray-900 dark:text-white'
                }`}
              >
                <div className="flex justify-between items-center">
                  <span>{option.location}</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {option.price === 0 ? 'Free' : `+${option.price}`}
                  </span>
                </div>
              </button>
            ))}

            {/* Render filtered locations */}
            {filteredLocations.map((location) => (
              <button
                key={location.location}
                onClick={() => {
                  onLocationSelect(location);
                  onToggleLocationDropdown();
                }}
                className={`w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 ${
                  selectedLocation?.location === location.location
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                    : 'text-gray-900 dark:text-white'
                }`}
              >
                <div className="flex justify-between items-center">
                  <span>{location.location}</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {location.price === 0 ? 'Free' : `+${location.price}`}
                  </span>
                </div>
              </button>
            ))}

            {/* Show message if no locations are found */}
            {filteredLocations.length === 0 && specialOptions.length === 0 && (
              <div className="px-4 py-2 text-gray-500 dark:text-gray-400 text-center">
                No locations found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}